import { ActionIcon, Group, Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
	Selector,
	SelectorTarget,
	SelectType,
} from '@repo/common/components/Selectors';
import { EntityType } from '@repo/common/enums/entityType';
import { Icon, IconButton } from '@repo/foundations';
import { Link } from 'react-router';
import {
	useAuthUser,
	useQuestionsEnabled,
	useReportList,
	useWorkspace,
} from '../../../../../../api';
import AnnouncementModal from '../../../../../../components/Announcement/AnnouncementModal';
import { TagSelector } from '../../../../../../components/EntityModal/Metadata/TagSelector';
import { entityModalStore } from '../../../../../../components/EntityModal/store';
import { SmallLoadingSpinner } from '../../../../../../components/LoadingSpinner';
import { WidgetType } from '../../../../../../interfaces';
import { useFeatureFlags } from '../../../../../../utils/featureFlags';
import type {
	HomepageWidgetType,
	WidgetRemoveFn,
	WidgetUpdateFn,
} from '../../../../types';
import {
	AI_SUGGESTIONS_WIDGET_FILTERS,
	COLLECTION_WIDGET_FILTERS,
	QUESTIONS_WIDGET_FILTERS,
	RESOURCE_WIDGET_FILTERS,
} from '../../constants';
import type { IWidgetItemProps } from '../../types';
import {
	isAISuggestionsWidget,
	isCollectionWidget,
	isQuestionsWidget,
	isResourceWidget,
	isTextWidget,
} from '../../utils';
import WidgetWrapper from './components/WidgetWrapper';
import {
	AISuggestionsWidget,
	RefreshAISuggestionsIconButton,
} from './widgets/AISuggestionsWidget';
import AnnouncementWidget from './widgets/AnnouncementWidget';
import CollectionWidget from './widgets/CollectionWidget';
import CollectionsWidget from './widgets/CollectionsWidget';
import { DataQualityScoreWidget } from './widgets/DataQualityScoreWidget';
import { DataQualityScoreWidgetV2 } from './widgets/DataQualityScoreWidgetV2';
import HelpfulLinksWidget from './widgets/HelpfulLinksWidget';
import InboxWidget from './widgets/InboxWidget';
import IntegrationsWidget from './widgets/IntegrationsWidget';
import QuestionsWidget from './widgets/QuestionsWidget';
import ResourcesWidget from './widgets/ResourcesWidget';
import SearchWidget from './widgets/SearchWidget';
import TextBlockWidget from './widgets/TextBlockWidget';

interface IWidgetProps extends IWidgetItemProps {
	onUpdateWidget: WidgetUpdateFn;
	onRemoveWidget: WidgetRemoveFn;
}

function Widget({ widget, onUpdateWidget, onRemoveWidget }: IWidgetProps) {
	const { workspace } = useWorkspace();
	const { user, isAdminUser } = useAuthUser();
	const widgetType = widget.type as HomepageWidgetType;
	const [opened, { open, close }] = useDisclosure(false);
	const [tagFilterOpened, { toggle: toggleTagFilter, close: closeTagFilter }] =
		useDisclosure(false);
	const questionsEnabled = useQuestionsEnabled();
	const { dqsV2 } = useFeatureFlags();
	let category: 'ai' | 'default' = 'default';

	const { data: reportData, isLoading: isLoadingReport } = useReportList({
		filters: {
			type: 'data_quality',
		},
		options: {
			enabled: widgetType === WidgetType.DATA_QUALITY_SCORE,
		},
	});

	let content = null;
	let filter = null;
	let right = null;

	if (isTextWidget(widgetType)) {
		content = (
			<TextBlockWidget
				widget={widget}
				onChange={onUpdateWidget(widget.id, 'content')}
			/>
		);
	}

	if (widgetType === WidgetType.INTEGRATIONS) {
		content = <IntegrationsWidget widget={widget} />;
	}

	if (widgetType === WidgetType.RECENT_SEARCHES) {
		content = <SearchWidget widget={widget} />;
	}

	if (widgetType === WidgetType.ANNOUNCEMENTS) {
		content = <AnnouncementWidget />;
		right = (
			<>
				<IconButton
					onClick={open}
					iconName="compose"
					variant="tertiary"
					tooltip="Create announcement"
				/>
				<AnnouncementModal opened={opened} close={close} />
			</>
		);
	}

	if (widgetType === WidgetType.INBOX) {
		content = <InboxWidget />;
	}

	if (widgetType === WidgetType.DATA_QUALITY_SCORE) {
		const hasTagFilter = !!widget.preferences?.tag_id;

		filter = dqsV2 ? (
			<Popover
				opened={tagFilterOpened}
				onClose={closeTagFilter}
				position="bottom-end"
				withArrow
				shadow="md"
			>
				<Popover.Target>
					<IconButton
						iconName="tag"
						variant="tertiary"
						tooltip={(() => {
							if (!isAdminUser) return 'Tag filter';
							return hasTagFilter ? 'Tag filter applied' : 'Filter by tag';
						})()}
						onClick={toggleTagFilter}
					/>
				</Popover.Target>
				<Popover.Dropdown>
					<TagSelector
						onChange={(tagId) => {
							onUpdateWidget(
								widget.id,
								'preferences'
							)({
								...widget.preferences,
								tag_id: tagId,
							});
							closeTagFilter();
						}}
						initialValue={widget.preferences?.tag_id}
						allowEmptyOption
						readOnly={!isAdminUser}
					/>
				</Popover.Dropdown>
			</Popover>
		) : null;

		content = dqsV2 ? (
			<DataQualityScoreWidgetV2
				widget={widget}
				href={`/analytics/${reportData?.results?.[0]?.id}`}
			/>
		) : (
			<DataQualityScoreWidget
				widget={widget}
				href={`/analytics/${reportData?.results?.[0]?.id}`}
			/>
		);

		if (isLoadingReport) {
			right = <SmallLoadingSpinner w="auto" h="auto" />;
		} else if (!!reportData?.results?.[0]?.id) {
			right = (
				<ActionIcon
					component={Link}
					to={`/analytics/${reportData?.results?.[0]?.id}`}
					variant="tertiary"
				>
					<Icon name="chevronRight" />
				</ActionIcon>
			);
		}
	}

	if (isQuestionsWidget(widgetType) && questionsEnabled) {
		filter = (
			<Selector
				initialValue={[widgetType]}
				getItems={() => Promise.resolve(QUESTIONS_WIDGET_FILTERS)}
				onChange={(values) =>
					values[0] && onUpdateWidget(widget.id, 'type')(values[0])
				}
				selectType={SelectType.Single}
				disableSearch
			>
				<SelectorTarget variant="tertiary" size="md">
					{
						QUESTIONS_WIDGET_FILTERS.find(
							(filters) => filters.value === widgetType
						)?.label
					}
				</SelectorTarget>
			</Selector>
		);

		content = <QuestionsWidget widget={widget} />;
		right = (
			<IconButton
				variant="tertiary"
				iconName="compose"
				tooltip="Ask a question"
				onClick={() =>
					entityModalStore.open({
						type: EntityType.question,
						metadata: {
							owners: [user.id],
						},
					})
				}
			/>
		);
	}

	if (isResourceWidget(widgetType)) {
		const widgetFilters = workspace?.quality_enabled
			? RESOURCE_WIDGET_FILTERS
			: RESOURCE_WIDGET_FILTERS.filter(
					(widgetFilter) =>
						widgetFilter.value !== WidgetType.LOWEST_QUALITY_SCORE
				);

		filter = (
			<Selector
				initialValue={[widgetType]}
				getItems={() => Promise.resolve(widgetFilters)}
				onChange={(values) =>
					values[0] && onUpdateWidget(widget.id, 'type')(values[0])
				}
				selectType={SelectType.Single}
				disableSearch
			>
				<SelectorTarget variant="tertiary" size="md">
					{widgetFilters.find((filters) => filters.value === widgetType)?.label}
				</SelectorTarget>
			</Selector>
		);

		content = (
			<ResourcesWidget
				widget={widget}
				onWidgetTypeChange={onUpdateWidget(widget.id, 'type')}
			/>
		);
	}

	if (isCollectionWidget(widgetType)) {
		filter = (
			<Selector
				initialValue={[widgetType]}
				getItems={() => Promise.resolve(COLLECTION_WIDGET_FILTERS)}
				onChange={(values) =>
					values[0] && onUpdateWidget(widget.id, 'type')(values[0])
				}
				selectType={SelectType.Single}
				disableSearch
			>
				<SelectorTarget variant="tertiary" size="md">
					{
						COLLECTION_WIDGET_FILTERS.find(
							(filters) => filters.value === widgetType
						)?.label
					}
				</SelectorTarget>
			</Selector>
		);

		content = (
			<CollectionsWidget
				widget={widget}
				onWidgetTypeChange={onUpdateWidget(widget.id, 'type')}
			/>
		);
	}

	if (widgetType === WidgetType.COLLECTION_RESOURCES) {
		content = (
			<CollectionWidget
				widget={widget}
				onCollectionChange={onUpdateWidget(widget.id, [
					'linked_entity',
					'title',
				])}
			/>
		);
	}

	if (widgetType === WidgetType.HELPFUL_LINKS) {
		content = <HelpfulLinksWidget widget={widget} />;
	}

	if (isAISuggestionsWidget(widgetType)) {
		filter = (
			<Group spacing="xs">
				<RefreshAISuggestionsIconButton widget={widget} />
				<Selector
					initialValue={[widgetType]}
					getItems={() => Promise.resolve(AI_SUGGESTIONS_WIDGET_FILTERS)}
					onChange={(values) =>
						values[0] && onUpdateWidget(widget.id, 'type')(values[0])
					}
					selectType={SelectType.Single}
					disableSearch
				>
					<SelectorTarget variant="tertiary" size="md">
						{
							AI_SUGGESTIONS_WIDGET_FILTERS.find(
								(filters) => filters.value === widgetType
							)?.label
						}
					</SelectorTarget>
				</Selector>
			</Group>
		);

		category = 'ai';

		content = <AISuggestionsWidget widget={widget} />;
	}

	return (
		<WidgetWrapper
			widget={widget}
			withMenu
			onUpdateWidget={onUpdateWidget}
			onRemoveWidget={onRemoveWidget}
			filter={filter}
			right={right}
			category={category}
		>
			{content}
		</WidgetWrapper>
	);
}

export default Widget;
